/**
 * AutomationCreateModal Component
 *
 * Multi-step modal for creating new automations.
 * Content-first design: shows templates and cloneable automations immediately.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  HiXMark,
  HiPlus,
  HiMagnifyingGlass,
  HiArrowLeft,
  HiRocketLaunch,
  HiCog6Tooth,
} from "react-icons/hi2";
import { FaListUl, FaGoogle, FaChrome } from "react-icons/fa6";
import useTaskCreate, { STEPS } from "../hooks/useTaskCreate";
import { TEMPLATES } from "../config/templates";
import { DEMO_AUTOMATIONS } from "../config/demos";
import { AUTOMATION_TYPES, isCatalogueTaskType } from "../config/automationTypes";
import { getCategoriesWithConfigs } from "../utils/configHelpers";
import TaskEditPanel from "./TaskEditPanel";
import CategoryEditView from "./CategoryEditView";
import FaviconImage from "./FaviconImage";
import ChromeExtensionIllustration from "./ChromeExtensionIllustration";
import cloudApi from "../../../../services/cloudApi";
import { useAuth } from "../../../../contexts/AuthContext";
import { MCP_AGENT_TILES } from "../../../ConnectClaude/connectClients";

// Chrome Web Store listing for the Ultimate Web Scraper extension
const CHROME_WEBSTORE_URL =
  "https://chromewebstore.google.com/detail/ultimate-web-scraper/pdeldjlcnhallaapdggcmhpailpnnkmg";

// ============================================================================
// TEMPLATE ICON MAP
// ============================================================================

const TEMPLATE_ICONS = {
  list: FaListUl,
  maps: FaGoogle,
  chrome: FaChrome,
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Content-first picker — tabbed view with templates and cloneable automations */
const TABS = { TEMPLATES: "templates", AUTOMATIONS: "automations" };

const ContentPicker = ({ onSelectTemplate, onClone, onBlank, onSelectDemo, isCreating, createError }) => {
  const [automations, setAutomations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(TABS.AUTOMATIONS);
  const { user } = useAuth();
  // Directory reviewer accounts (reviewer*@ultimatewebscraper.com) must not see Google Maps scraping
  const isReviewer = /reviewer@ultimatewebscraper\.com$/i.test(user?.email || "");
  const demos = isReviewer ? DEMO_AUTOMATIONS.filter((d) => d.id !== "demo-google-maps-cafe") : DEMO_AUTOMATIONS;

  useEffect(() => {
    const load = async () => {
      try {
        const response = await cloudApi.getAutomations({ limit: 200, offset: 0 });
        // Catalogue automations (Shopify, WooCommerce, Magento, Salesforce Commerce Cloud) are
        // authored by the MCP agent (store detection / collection resolution) and can't be rebuilt
        // or created from this dashboard, so they aren't cloneable.
        const cloneable = (response.automations || []).filter((a) => !isCatalogueTaskType(a.type));
        setAutomations(cloneable);
      } catch (error) {
        setLoadError(error.message || "Failed to load automations");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const query = search.toLowerCase().trim();

  const filteredTemplates = query
    ? TEMPLATES.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query)
      )
    : TEMPLATES;

  const filteredAutomations = query
    ? automations.filter(
        (a) =>
          a.name?.toLowerCase().includes(query) ||
          a.website?.toLowerCase().includes(query)
      )
    : automations;

  const noResults =
    activeTab === TABS.TEMPLATES
      ? query && filteredTemplates.length === 0
      : query && filteredAutomations.length === 0;

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-xl mx-auto space-y-4 animate-stagger-in" style={{ animationDelay: '50ms' }}>
        {/* Tabs — hidden for now, keeping functionality intact */}
        {/* <div className="flex gap-1 p-1 bg-gray-100 dark:bg-white/[0.04] rounded-xl">
          {[
            { id: TABS.AUTOMATIONS, label: "Your Automations" },
            { id: TABS.TEMPLATES, label: "Templates" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-white dark:bg-white/[0.08] text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div> */}

        {/* Search — only shown when user has automations */}
        {automations.length > 0 && (
          <div className="relative">
            <HiMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search your automations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-9 py-3 text-sm rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <HiXMark className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {createError && (
          <p className="text-xs text-red-500 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            {createError}
          </p>
        )}

        {/* No results */}
        {noResults && (
          <div className="text-center py-8">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No results for &ldquo;
              <span className="text-gray-600 dark:text-gray-300">{search}</span>
              &rdquo;
            </p>
          </div>
        )}

        {/* Templates tab */}
        {activeTab === TABS.TEMPLATES && filteredTemplates.length > 0 && (
          <div className="space-y-1.5">
            {filteredTemplates.map((template) => {
              const Icon = TEMPLATE_ICONS[template.icon] || HiCog6Tooth;
              return (
                <button
                  key={template.id}
                  onClick={() => onSelectTemplate(template.id)}
                  className="w-full flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-emerald-400/50 hover:bg-emerald-50/50 dark:hover:bg-emerald-500/[0.06] transition-all duration-200 text-left group"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors truncate">
                      {template.name}
                    </p>
                    <p className="text-[13px] text-gray-500 dark:text-gray-400 truncate">
                      {template.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Your Automations tab */}
        {activeTab === TABS.AUTOMATIONS && (
          <>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-100 dark:border-white/[0.04] animate-pulse"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-white/[0.06]" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-2/3" />
                      <div className="h-3.5 bg-gray-100 dark:bg-white/[0.04] rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : loadError ? (
              <p className="text-sm text-red-500 py-4 text-center">{loadError}</p>
            ) : filteredAutomations.length === 0 && !query ? (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                  Try a demo to see how it works
                </p>
                {demos.map((demo) => (
                  <button
                    key={demo.id}
                    onClick={() => onSelectDemo(demo.id)}
                    disabled={isCreating}
                    className="w-full flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-emerald-400/50 hover:bg-emerald-50/30 dark:hover:bg-emerald-500/[0.06] transition-all duration-200 text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {demo.favicon ? (
                      <FaviconImage
                        src={demo.favicon}
                        alt=""
                        className="w-8 h-8 rounded-lg flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                          {demo.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {demo.name}
                      </p>
                      <p className="text-[13px] text-gray-400 dark:text-gray-500 truncate">
                        {demo.website}
                      </p>
                    </div>
                    <span className="flex-shrink-0 transition-all duration-200">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 group-hover:hidden">Demo</span>
                      <span className="hidden group-hover:flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Run
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : filteredAutomations.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                No matching automations
              </p>
            ) : (
              <div className="space-y-1.5">
                {filteredAutomations.map((automation) => (
                  <button
                    key={automation.id}
                    onClick={() => onClone(automation.id)}
                    className="w-full flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-purple-400/50 hover:bg-purple-50/30 dark:hover:bg-purple-500/[0.06] transition-all duration-200 text-left"
                  >
                    {automation.favicon ? (
                      <FaviconImage
                        src={automation.favicon}
                        alt=""
                        className="w-8 h-8 rounded-lg flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                          {(automation.name || "A").charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {automation.name || "Untitled"}
                      </p>
                      {automation.website && (
                        <p className="text-[13px] text-gray-400 dark:text-gray-500 truncate">
                          {automation.website}
                        </p>
                      )}
                    </div>
                    <span className="text-xs font-medium text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {automation.type === "extract_list" ? "List" : "Pages"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Chrome Extension Onboarding */}
        {!query && (
          <div className="pt-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-200 dark:bg-white/[0.08]" />
              <span className="text-xs text-gray-400 dark:text-gray-500">or</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-white/[0.08]" />
            </div>
            <div className="rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-gray-50/50 dark:bg-white/[0.01] p-5 pb-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 text-center">
                Open the Chrome Extension
              </p>
              {/* Illustration */}
              <div className="mb-4">
                <ChromeExtensionIllustration />
              </div>
              {/* Steps */}
              <div className="flex items-start justify-center gap-4 text-center">
                {[
                  { step: 1, text: "Open List Extractor, Page Extractor", gradient: "from-blue-500 to-blue-600" },
                  { step: 2, text: "Configure your extraction", gradient: "from-cyan-500 to-cyan-600" },
                  { step: 3, text: "Click cloud to run it here", gradient: "from-purple-500 to-purple-600" },
                ].map(({ step, text, gradient }) => (
                  <div key={step} className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
                    <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-[10px] font-bold text-white">{step}</span>
                    </div>
                    <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400">{text}</p>
                  </div>
                ))}
              </div>

              {/* Don't have the extension yet? */}
              <div className="mt-4 pt-3 border-t border-gray-200/60 dark:border-white/[0.06] flex items-center justify-center gap-2">
                <span className="text-[11px] text-gray-400 dark:text-gray-500">Don&apos;t have it yet?</span>
                <a
                  href={CHROME_WEBSTORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
                >
                  <FaChrome className="w-3 h-3" />
                  Install the Chrome extension
                </a>
              </div>
            </div>

            {/* AI agent path — same tiles EmptyState / SignIn show */}
            <Link
              to="/dashboard/integrations"
              className="group mt-3 flex items-center gap-3.5 p-3.5 rounded-xl border border-violet-200/60 dark:border-violet-500/15 bg-white dark:bg-white/[0.02] hover:border-violet-400/50 hover:bg-violet-50/30 dark:hover:bg-violet-500/[0.06] transition-all duration-200"
            >
              <div className="flex -space-x-1.5 flex-shrink-0" aria-hidden="true">
                {MCP_AGENT_TILES.map(({ name, Mark }) => (
                  <div
                    key={name}
                    title={name}
                    className="w-7 h-7 rounded-lg bg-white dark:bg-gray-900 ring-1 ring-black/[0.06] dark:ring-white/10 shadow-sm flex items-center justify-center"
                  >
                    <Mark className="w-4 h-4" />
                  </div>
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                  Prefer to just ask?
                </p>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 truncate">
                  Connect Claude, ChatGPT, Cursor, or any MCP agent
                </p>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 dark:text-violet-400 flex-shrink-0">
                Connect
                <svg className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

/** Blank path — pick automation type */
const TypePicker = ({ onSelect }) => {
  const types = [
    { key: "listExtractor", ...AUTOMATION_TYPES.listExtractor },
    { key: "pageExtractor", ...AUTOMATION_TYPES.pageExtractor },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-lg mx-auto space-y-4">
        <h3 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          Choose automation type
        </h3>
        <div className="space-y-3">
          {types.map(({ key, name, icon: Icon, colorClasses, description }, index) => (
            <button
              key={key}
              onClick={() => onSelect(key)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] ${colorClasses.hoverBorder} transition-all duration-200 text-left group animate-stagger-in`}
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${colorClasses.bg} border ${colorClasses.border} flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${colorClasses.text}`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white transition-colors">
                  {name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {description}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Template path — simplified field form */
const TemplateForm = ({ template, onCreateAndRun, onCustomize, isCreating, createError }) => {
  const [inputs, setInputs] = useState(() => {
    const init = {};
    template.fields.forEach((f) => {
      init[f.id] = "";
    });
    return init;
  });

  const isValid = template.fields
    .filter((f) => f.required)
    .every((f) => inputs[f.id]?.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid) onCreateAndRun(inputs);
  };

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Template header */}
        <div
          className="flex items-center gap-3 p-4 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] animate-stagger-in"
          style={{ animationDelay: '0ms' }}
        >
          {(() => {
            const Icon = TEMPLATE_ICONS[template.icon] || HiCog6Tooth;
            return (
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Icon className="w-5 h-5 text-emerald-400" />
              </div>
            );
          })()}
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {template.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {template.description}
            </p>
          </div>
        </div>

        {/* Fields */}
        <div
          className="p-4 rounded-xl border border-gray-200/60 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] animate-stagger-in"
          style={{ animationDelay: '60ms' }}
        >
        <form onSubmit={handleSubmit} className="space-y-4">
          {template.fields.map((field) => (
            <div key={field.id}>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                {field.label}
                {field.required && <span className="text-red-400 ml-0.5">*</span>}
              </label>
              <input
                type={field.type || "text"}
                placeholder={field.placeholder}
                value={inputs[field.id]}
                onChange={(e) => setInputs((prev) => ({ ...prev, [field.id]: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
              />
            </div>
          ))}

          {createError && (
            <p className="text-sm text-red-500 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              {createError}
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2">
            <button
              type="submit"
              disabled={!isValid || isCreating}
              className="w-full flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-sm shadow-blue-600/20 hover:shadow-md transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <HiRocketLaunch className="w-4 h-4" />
                  Create & Run
                </>
              )}
            </button>
            <button
              type="button"
              disabled={!isValid || isCreating}
              onClick={() => onCustomize(inputs)}
              className="w-full text-center text-xs text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed py-1"
            >
              Customize settings before creating
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

/** Full configuration editor — reuses existing TaskEditPanel + CategoryEditView */
const ConfigEditor = ({ taskConfig, onConfigChange, createError }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const categories = taskConfig ? getCategoriesWithConfigs(taskConfig) : [];

  // Auto-select General category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      const general = categories.find((c) => c.id === "general");
      if (general) setActiveCategory(general);
    }
  }, [categories.length]);

  const handleConfigChange = (configId, newConfig) => {
    onConfigChange(configId, newConfig);
    if (configId === "general" && newConfig.name !== undefined) {
      onConfigChange("metadata", {
        ...taskConfig.metadata,
        title: newConfig.name,
      });
    }
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col lg:flex-row bg-white/30 dark:bg-[#0c0c0f]">
      {/* Left: categories */}
      <div
        className={`
          ${activeCategory ? "hidden lg:block" : "block"}
          lg:w-64 lg:flex-shrink-0 lg:border-r lg:border-gray-200/60 lg:dark:border-white/[0.06]
          h-full overflow-y-auto bg-white dark:bg-[#0c0c0f]
        `}
      >
        <TaskEditPanel
          categories={categories}
          onCategoryEdit={setActiveCategory}
          selectedCategory={activeCategory}
        />
      </div>

      {/* Right: category detail */}
      <div
        className={`
          ${!activeCategory ? "hidden lg:flex lg:items-center lg:justify-center" : "flex-1 flex flex-col"}
          h-full overflow-hidden bg-white dark:bg-[#0c0c0f]/80
        `}
      >
        {activeCategory ? (
          <CategoryEditView
            categoryDisplay={activeCategory}
            configItems={activeCategory.configItems}
            taskConfig={taskConfig}
            onConfigChange={handleConfigChange}
            onBack={() => setActiveCategory(null)}
          />
        ) : (
          <div className="text-center">
            <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">
              Select a category to configure
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/** Preview step — simplified view for demos with Run button */
const PreviewCard = ({ taskConfig, onRun, onCustomize, isCreating, createError }) => {
  const name = taskConfig.general?.name || taskConfig.metadata?.title || "Untitled";
  const url = taskConfig.metadata?.websiteUrl || taskConfig.automation?.url || "";
  const favicon = taskConfig.metadata?.websiteFavicon || "";
  const automationType = taskConfig.automationType;
  const typeInfo = AUTOMATION_TYPES[automationType];

  const paginationLabel = automationType === "listExtractor"
    ? ({ scroll: "Auto-Scroll", navigate: "Page Navigation", click_button: "Load More" }[taskConfig.automation?.paginationMethod] || "None")
    : `${taskConfig.automation?.urls?.length || 0} URLs`;

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-lg mx-auto space-y-4 animate-stagger-in" style={{ animationDelay: '50ms' }}>
        {/* Preview Card */}
        <div className="rounded-2xl overflow-hidden border border-gray-200/60 dark:border-white/[0.08] bg-white dark:bg-white/[0.02]">
          {/* Ready to Run banner */}
          <div className="bg-gradient-to-r from-cyan-50 to-transparent dark:from-cyan-500/10 dark:to-transparent px-4 py-2.5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse shadow-sm shadow-cyan-500/50" />
              <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wide">
                Ready to Run
              </span>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Header: favicon + name + URL */}
            <div className="flex items-center gap-3">
              {favicon && (
                <FaviconImage src={favicon} alt="" className="w-10 h-10 rounded-xl flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{name}</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{url}</p>
              </div>
            </div>

            {/* 2x2 Stats Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06]">
                <div className="text-[11px] text-gray-400 font-medium uppercase mb-0.5">Type</div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{typeInfo?.name || "List Extractor"}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06]">
                <div className="text-[11px] text-gray-400 font-medium uppercase mb-0.5">
                  {automationType === "listExtractor" ? "Pagination" : "URLs"}
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{paginationLabel}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06]">
                <div className="text-[11px] text-gray-400 font-medium uppercase mb-0.5">Schedule</div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">None</p>
              </div>
              <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06]">
                <div className="text-[11px] text-gray-400 font-medium uppercase mb-0.5">First Run</div>
                <p className="text-sm font-semibold text-amber-500">Now</p>
              </div>
            </div>
          </div>
        </div>

        {createError && (
          <p className="text-sm text-red-500 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            {createError}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onRun}
            disabled={isCreating}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 hover:from-blue-500 hover:via-cyan-500 hover:to-blue-500 text-white rounded-xl shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-cyan-600/25 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
          >
            {isCreating ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <HiRocketLaunch className="w-4 h-4" />
                Run Now
              </>
            )}
          </button>
          <button
            onClick={onCustomize}
            disabled={isCreating}
            className="w-full text-center text-xs text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed py-1"
          >
            Customize settings
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN MODAL
// ============================================================================

const AutomationCreateModal = ({ onClose, onCreated, onNoCredits, onMixedDomains }) => {
  const {
    step,
    taskConfig,
    isCreating,
    createError,
    selectedTemplate,
    initBlank,
    initFromClone,
    initFromDemo,
    selectTemplate,
    applyTemplateInputs,
    updateTaskSection,
    goToStep,
    reset,
    createTask,
  } = useTaskCreate({ onCreated });

  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 120);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  // Determine header title and whether to show back button
  const getHeaderInfo = () => {
    switch (step) {
      case STEPS.METHOD:
        return { title: "New Automation", showBack: false };
      case STEPS.PICK_TYPE:
        return { title: "Choose Type", showBack: true, backStep: STEPS.METHOD };
      case STEPS.TEMPLATE_FORM:
        return { title: selectedTemplate?.name || "Template", showBack: true, backStep: STEPS.METHOD };
      case STEPS.PREVIEW:
        return { title: taskConfig?.general?.name || "Preview", showBack: true, backStep: STEPS.METHOD };
      case STEPS.CONFIGURE:
        return { title: "Configure Automation", showBack: true, backStep: STEPS.METHOD };
      default:
        return { title: "New Automation", showBack: false };
    }
  };

  const headerInfo = getHeaderInfo();

  // Handle "Create & Run" from template form
  const handleTemplateCreateAndRun = async (inputs) => {
    const config = applyTemplateInputs(inputs);
    if (!config) return;
    try {
      const result = await createTask(true, config);
      if (result) onClose();
    } catch (error) {
      if (error.code === 'INSUFFICIENT_CREDITS' || error.code === 'PLAN_REQUIRED') {
        onClose();
        onNoCredits?.();
      } else if (error.code === 'MIXED_DOMAINS') {
        onClose();
        onMixedDomains?.();
      }
    }
  };

  // Handle "Customize" from template form — build config and go to editor
  const handleTemplateCustomize = (inputs) => {
    applyTemplateInputs(inputs);
    goToStep(STEPS.CONFIGURE);
  };

  // Handle clicking a demo — build config and run immediately
  const handleDemoRun = async (demoId) => {
    const config = initFromDemo(demoId);
    if (!config) return;
    try {
      const result = await createTask(true, config);
      if (result) onClose();
    } catch (error) {
      if (error.code === 'INSUFFICIENT_CREDITS' || error.code === 'PLAN_REQUIRED') {
        onClose();
        onNoCredits?.();
      } else if (error.code === 'MIXED_DOMAINS') {
        onClose();
        onMixedDomains?.();
      }
    }
  };

  // Handle create from the full editor
  const handleCreate = async (runImmediately = false) => {
    try {
      const result = await createTask(runImmediately);
      if (result) onClose();
    } catch (error) {
      if (error.code === 'INSUFFICIENT_CREDITS' || error.code === 'PLAN_REQUIRED') {
        onClose();
        onNoCredits?.();
      } else if (error.code === 'MIXED_DOMAINS') {
        onClose();
        onMixedDomains?.();
      }
    }
  };

  const handleBack = () => {
    if (headerInfo.backStep) {
      reset();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-120 ${closing ? 'opacity-0' : 'opacity-100'}`}
      onClick={handleClose}
    >
      <div
        className={`bg-white dark:bg-[#0c0c0f] rounded-2xl shadow-2xl w-full ${step === STEPS.CONFIGURE ? 'max-w-3xl xl:max-w-4xl' : 'max-w-lg'} h-[75vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-white/[0.08] transition-[max-width] duration-200 ease-out ${closing ? 'transition-all duration-120 scale-[0.98] opacity-0' : 'animate-modal-in'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient accent bar */}
        <div className="h-[2px] w-full bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 opacity-60 flex-shrink-0" />

        {/* Header */}
        <div className="px-5 py-3 flex items-center justify-between flex-shrink-0 border-b border-gray-100 dark:border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            {headerInfo.showBack && (
              <button
                onClick={handleBack}
                className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <HiArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {headerInfo.title}
            </h2>
            <span className="text-xs text-gray-400 dark:text-gray-600 truncate">
              {step === STEPS.METHOD && "Clone an automation, use the extension, or ask your AI agent"}
              {step === STEPS.PICK_TYPE && "Select extraction type"}
              {step === STEPS.TEMPLATE_FORM && (selectedTemplate?.description || "Fill in the required fields")}
              {step === STEPS.PREVIEW && "Review and run your automation"}
              {step === STEPS.CONFIGURE && "Configure your automation settings"}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <HiXMark className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        {step === STEPS.METHOD && (
          <ContentPicker
            onSelectTemplate={selectTemplate}
            onClone={initFromClone}
            onBlank={() => goToStep(STEPS.PICK_TYPE)}
            onSelectDemo={handleDemoRun}
            isCreating={isCreating}
            createError={createError}
          />
        )}

        {step === STEPS.PICK_TYPE && (
          <TypePicker onSelect={initBlank} />
        )}

        {step === STEPS.TEMPLATE_FORM && selectedTemplate && (
          <TemplateForm
            template={selectedTemplate}
            onCreateAndRun={handleTemplateCreateAndRun}
            onCustomize={handleTemplateCustomize}
            isCreating={isCreating}
            createError={createError}
          />
        )}

        {step === STEPS.PREVIEW && taskConfig && (
          <PreviewCard
            taskConfig={taskConfig}
            onRun={handlePreviewRun}
            onCustomize={handlePreviewCustomize}
            isCreating={isCreating}
            createError={createError}
          />
        )}

        {step === STEPS.CONFIGURE && taskConfig && (
          <ConfigEditor
            taskConfig={taskConfig}
            onConfigChange={updateTaskSection}
            createError={createError}
          />
        )}

        {/* Footer — shown only in configure step */}
        {step === STEPS.CONFIGURE && (
          <div className="px-5 py-3 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/30 dark:bg-white/[0.01] flex items-center justify-between flex-shrink-0">
            <div className="text-sm">
              {createError && (
                <span className="text-red-500 dark:text-red-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  {createError}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleClose}
                disabled={isCreating}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCreate(false)}
                disabled={isCreating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-white/[0.06] hover:bg-gray-200 dark:hover:bg-white/[0.08] rounded-lg border border-gray-200 dark:border-white/[0.08] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Create
              </button>
              <button
                onClick={() => handleCreate(true)}
                disabled={isCreating}
                className="px-5 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-sm shadow-blue-600/20 hover:shadow-md hover:shadow-blue-600/25 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2"
              >
                {isCreating ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <HiRocketLaunch className="w-4 h-4" />
                    Create & Run
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationCreateModal;
